import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalShellComponent } from '../modal-shell/modal-shell.component';
import { LoaderComponent } from '../../common/loader/loader.component';
import { TenantsService } from '../../services/tenants.service';
import { ToastrService } from '../../services/toastr.service';
import { TenantServiceAssignment } from '../../models/tenant-service.model';
import { ApiErrorResponse } from '../../models/auth.model';

export type TenantServiceModalMode = 'add' | 'edit' | 'delete';

@Component({
  selector: 'app-tenant-services-modal',
  imports: [ModalShellComponent, ReactiveFormsModule, LoaderComponent],
  templateUrl: './tenant-services-modal.component.html',
  styleUrl: './tenant-services-modal.component.scss'
})
export class TenantServicesModalComponent implements OnInit {
  @Input() mode: TenantServiceModalMode = 'add';
  @Input() tenantUid = '';
  @Input() service: TenantServiceAssignment | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  form!: FormGroup<{ serviceId: FormControl<number | null>; isActive: FormControl<boolean | null> }>;
  loading = false;

  constructor(private fb: FormBuilder, private tenantsService: TenantsService, private toastrService: ToastrService) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      serviceId: [this.service?.service ?? null, this.mode === 'add' ? [Validators.required] : []],
      isActive: [this.service?.is_active ?? true]
    });

    if (this.mode === 'edit') {
      this.form.controls.serviceId.disable();
    }
  }

  get title(): string {
    if (this.mode === 'add') return 'Add Service';
    if (this.mode === 'edit') return 'Edit Service';
    return 'Remove Service';
  }

  submit(): void {
    if (this.mode === 'delete') {
      this.runDelete();
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.mode === 'add') {
      this.runCreate();
    } else {
      this.runUpdate();
    }
  }

  close(): void {
    if (this.loading) {
      return;
    }
    this.closed.emit();
  }

  private runCreate(): void {
    this.loading = true;

    const { serviceId } = this.form.getRawValue();

    this.tenantsService.createTenantService({ tenant: this.tenantUid, service: serviceId! }).subscribe({
      next: () => this.finish(),
      error: (err: HttpErrorResponse) => this.fail(err)
    });
  }

  private runUpdate(): void {
    if (!this.service) {
      return;
    }
    this.loading = true;

    const { isActive } = this.form.getRawValue();

    this.tenantsService.updateTenantService(this.service.id, { is_active: !!isActive }).subscribe({
      next: () => this.finish(),
      error: (err: HttpErrorResponse) => this.fail(err)
    });
  }

  private runDelete(): void {
    if (!this.service) {
      return;
    }
    this.loading = true;

    this.tenantsService.deleteTenantService(this.service.id).subscribe({
      next: () => this.finish(),
      error: (err: HttpErrorResponse) => this.fail(err)
    });
  }

  private finish(): void {
    this.loading = false;
    this.saved.emit();
    this.closed.emit();
  }

  private fail(err: HttpErrorResponse): void {
    this.loading = false;
    const body = err.error as ApiErrorResponse | undefined;
    this.toastrService.error(body?.detail ?? body?.error?.message ?? 'Something went wrong. Please try again.');
  }
}
